import { Cancel, Lock, Save } from "@mui/icons-material";
import {
  Button,
  LinearProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ReactNode, useMemo } from "react";
import {
  NavigateFunction,
  useFetchers,
  useLocation,
  useNavigate,
} from "react-router";
import ReactGA from "react-ga4";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";

export interface FormButtonsProps {
  disableSubmit?: boolean;
  readonly?: boolean;
  handleSubmitAction?: () => void;
  handleCancelAction?: () => void;
  submitButtonLabel?: string;
  cancelButtonLabel?: string;
  submitButtonIcon?: ReactNode;
  cancelButtonIcon?: ReactNode;
}

const FormButtons = ({
  readonly = false,
  disableSubmit = false,
  handleSubmitAction,
  handleCancelAction,
  submitButtonLabel,
  cancelButtonLabel,
  submitButtonIcon,
  cancelButtonIcon,
}: FormButtonsProps): ReactNode => {
  const navigate: NavigateFunction = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const fetchers = useFetchers();
  const { breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down("lg"));
  const saving = useMemo(() => {
    return fetchers.some((fetcher) => fetcher.state === "submitting");
  }, [fetchers]);

  const loading = useMemo(() => {
    return fetchers.some((fetcher) => fetcher.state === "loading");
  }, [fetchers]);

  const rightButtonText: string = useMemo(() => {
    if (submitButtonLabel && !mobile) {
      return submitButtonLabel;
    }
    let firstWord: string;
    if (pathname.includes("edit") || pathname.includes("user-settings")) {
      firstWord = t("update");
    } else if (pathname.includes("create")) {
      firstWord = t("create");
    } else {
      firstWord = t("submit");
    }
    return firstWord;
  }, [pathname, t, submitButtonLabel]);

  return (
    <Stack flex={1} spacing={0} p={0}>
      {(saving || loading) && mobile && (
        <LinearProgress
          variant="indeterminate"
          sx={{ width: "100%", height: 5 }}
          color="primary"
        />
      )}
      <Stack
        flexDirection={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        display={"flex"}
        sx={{
          width: "100%",
          p: 2,
        }}
      >
        <Stack
          flexDirection={"row"}
          justifyContent={mobile ? "space-between" : "flex-end"}
          display={"flex"}
          flex={1}
          p={0}
          gap={2}
        >
          {handleCancelAction && (
            <Button
              sx={{
                flex: { xs: 1, lg: "none" },
                borderRadius: "8px",
                px: 2,
                py: 1.25,
              }}
              color="secondary"
              variant={"outlined"}
              startIcon={cancelButtonIcon || <Cancel />}
              fullWidth={mobile}
              aria-label={t("cancel")}
              onClick={() => {
                if (handleCancelAction) {
                  handleCancelAction();
                } else {
                  navigate(-1);
                }
              }}
            >
              {cancelButtonLabel || t("cancel")}
            </Button>
          )}
          <LoadingButton
            sx={{
              flex: { xs: 1, lg: "none" },
              borderRadius: "8px",
              px: 2,
              py: 1.25,
            }}
            color="secondary"
            loading={saving}
            loadingIndicator={`${t("saving")}...`}
            variant={"contained"}
            type="submit"
            endIcon={readonly ? <Lock /> : submitButtonIcon || <Save />}
            fullWidth={mobile}
            onClick={() => {
              if (handleSubmitAction) {
                handleSubmitAction();
                ReactGA.event({
                  category: "Form",
                  action: "Submit",
                  label: rightButtonText,
                });
              }
            }}
            aria-label={rightButtonText}
            disabled={readonly || disableSubmit}
          >
            {rightButtonText}
          </LoadingButton>
        </Stack>
      </Stack>
      {(saving || loading) && mobile && (
        <LinearProgress
          variant="indeterminate"
          sx={{ width: "100%", height: 5 }}
          color="info"
        />
      )}
    </Stack>
  );
};

export default FormButtons;
